// src/pages/ProductDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useCart } from "../context/CartContext";

function ProductDetailPage() {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const handleBuy = () => {
    addToCart({
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      mainImage: product.mainImage,
      brand: product.brand,
    });
    navigate("/panier");
  };
  const { slug } = useParams();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [loading, setLoading] = useState(true);

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
      }),
    []
  );

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "products_prosoft"),
          where("slug", "==", slug),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          const data = { id: docSnap.id, ...docSnap.data() };
          setProduct(data);
          const firstImage =
            data.mainImage || (data.images && data.images[0]) || "";
          setMainImage(firstImage);
        } else {
          setProduct(null);
        }
      } catch (e) {
        console.error("Erreur chargement produit", e);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="container py-5">
        <p>Chargement du produit...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-5">
        <p>Produit introuvable.</p>
        <Link to="/" className="btn btn-outline-secondary btn-sm mt-3">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const whatsappMessage = `Bonjour, je souhaite des informations ou acheter : ${product.name}`;
  const whatsappUrl = `https://wa.me/22893362525?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const handleAddToCart = () => {
    addItem(product, 1);
    // alert("Produit ajouté au panier. Vous pouvez finaliser dans le panier.");
  };

  const handleBuyNow = () => {
    addItem(product, 1);
    navigate("/panier");
  };

  return (
    <div className="product-detail-page py-4">
      <div className="container">
        {/* Fil d’Ariane */}
        <nav className="mb-3 small text-muted">
          <Link to="/">Accueil</Link> /{" "}
          {product.categorySlug ? (
            <>
              <Link to={`/categorie/${product.categorySlug}`}>
                {product.categorySlug}
              </Link>{" "}
              /{" "}
            </>
          ) : null}
          <span>{product.name}</span>
        </nav>

        <div className="row g-4">
          {/* Galerie images */}
          <div className="col-lg-5">
            <div className="pd-gallery-main mb-3">
              {mainImage ? (
                <img src={mainImage} alt={product.name} />
              ) : (
                <div className="ratio ratio-4x3 bg-light d-flex align-items-center justify-content-center">
                  <span className="text-muted small">Pas d'image</span>
                </div>
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="pd-gallery-thumbs d-flex gap-2 flex-wrap">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`pd-thumb-item ${img === mainImage ? "active" : ""
                      }`}
                    onClick={() => setMainImage(img)}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos produit */}
          <div className="col-lg-7">
            <h1 className="h4 mb-2">{product.name}</h1>

            <div className="mb-2 small text-muted">
              {product.brand && (
                <>
                  Marque : <strong>{product.brand}</strong>
                </>
              )}
            </div>

            {/* Prix */}
            <div className="pd-price-block mb-3">
              <div className="pd-price-current">
                {fmt.format(product.price || 0)} FCFA
              </div>
              {product.oldPrice && (
                <div className="pd-price-old">
                  {fmt.format(product.oldPrice)} FCFA
                </div>
              )}
            </div>

            {/* Stock */}
            <div className="mb-3 small">
              {product.inStock ? (
                <span className="text-success fw-semibold">En stock</span>
              ) : (
                <span className="text-danger fw-semibold">
                  Rupture de stock
                </span>
              )}
            </div>

            {/* Description courte */}
            {product.shortDescription && (
              <p className="small text-muted">{product.shortDescription}</p>
            )}

            {/* Caractéristiques techniques (specs) */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="mb-3">
                <h6 className="fw-bold mb-2">
                  Caractéristiques principales
                </h6>
                <ul className="small ps-3">
                  {Object.entries(product.specs).map(([key, value]) => {
                    if (
                      value === undefined ||
                      value === null ||
                      value === "" ||
                      value === false
                    ) {
                      return null;
                    }
                    const label = key.replace(/([A-Z])/g, " $1");
                    return (
                      <li key={key}>
                        <strong>{label} :</strong>{" "}
                        {typeof value === "boolean"
                          ? value
                            ? "Oui"
                            : "Non"
                          : String(value)}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Description longue */}
            {product.description && (
              <div className="mb-3 small">
                <h6 className="fw-bold mb-2">Description</h6>
                <p className="text-muted">{product.description}</p>
              </div>
            )}

            {/* Boutons d’action */}
            <div className="d-flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBuyNow}
              >
                J'achète maintenant
              </button>

              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={handleAddToCart}
              >
                Ajouter au panier
              </button>

              <a
                href={whatsappUrl}
                className="btn btn-outline-success"
                target="_blank"
                rel="noreferrer"
              >
                Contacter via WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Ici tu pourras rajouter “Produits similaires” avec ps-product-card */}
      </div>
    </div>
  );
}

export default ProductDetailPage;

